/**
 * Welle 120 — Anlagen (nur PDF): aus der Dropbox wählen oder hochladen (→ …/Rechnungen/Anlagen/<id>/).
 * Sie werden in der Vorschau und beim Stellen hinten an die Rechnung gehängt.
 * Welle 121 (Ingvar 08.10.): Der Dropbox-Browser startet im Kreditoren-Ordner des Ausstellers („#Kreditoren FBG"),
 * merkt sich den letzten Ordner je Aussteller, sucht (Ordner sofort, Unterordner per Dropbox-Suche) und bleibt für
 * mehrere Anlagen offen. Hochladen: Dateien VOR dem Leeren des Felds übernehmen (sonst ist die FileList leer).
 */
import React, { useState } from "react";
import { API_BASE } from "../../api/config";
import { API, json, dropboxDatei } from "./hilfen";
import Ico from "./Ico";

const SCHREIBEN_API = `${API_BASE}/mietverwaltung/api/schreiben`;
const WURZEL = "/Holthey Immobilien";
const MERKEN = "imho_rechnung_anlagen_ordner";

const gemerkt = (aussteller) => {
  try { return (JSON.parse(localStorage.getItem(MERKEN) || "{}") || {})[aussteller || "-"] || ""; } catch { return ""; }
};
const merken = (aussteller, pfad) => {
  try {
    const alle = JSON.parse(localStorage.getItem(MERKEN) || "{}") || {};
    alle[aussteller || "-"] = pfad;
    localStorage.setItem(MERKEN, JSON.stringify(alle));
  } catch { /* privates Fenster o. ä. — dann eben ohne Merken */ }
};

export default function AnlagenBlock({ form, setForm, gesperrt, sicherstellenId, zeige, nachUpload }) {
  const [browser, setBrowser] = useState(null);     // {pfad, ordner, dateien}
  const [start, setStart] = useState(null);         // {pfad, kreditoren} des Ausstellers
  const [filter, setFilter] = useState("");
  const [treffer, setTreffer] = useState(null);     // Ergebnis der Suche in Unterordnern
  const [laedt, setLaedt] = useState(false);
  const [busy, setBusy] = useState(false);
  const anlagen = form.anlagen || [];
  const aussteller = form.aussteller || "";

  const browse = async (pfad) => {
    setLaedt(true);
    try {
      const d = await json(await fetch(`${SCHREIBEN_API}/anlagen/browse?path=${encodeURIComponent(pfad || WURZEL)}`));
      setBrowser(d); setFilter(""); setTreffer(null);
      merken(aussteller, d.pfad);
    } catch (e) { zeige(false, String(e.message || e)); }
    setLaedt(false);
  };
  const oeffnen = async () => {
    let s = start;
    if (!s || s.aussteller !== aussteller) {
      try { s = { ...(await json(await fetch(`${API}/anlagen/start?aussteller=${encodeURIComponent(aussteller)}`))), aussteller }; }
      catch { s = { pfad: WURZEL, kreditoren: false, aussteller }; }
      setStart(s);
    }
    const zuletzt = gemerkt(aussteller);
    await browse(zuletzt || s.pfad || WURZEL);
  };
  const suchen = async () => {
    const q = filter.trim();
    if (q.length < 2 || !browser) return;
    setLaedt(true);
    try {
      const d = await json(await fetch(`${SCHREIBEN_API}/anlagen/suche?path=${encodeURIComponent(browser.pfad)}&q=${encodeURIComponent(q)}`));
      setTreffer(d.dateien || []);
    } catch (e) { zeige(false, String(e.message || e)); }
    setLaedt(false);
  };

  const dazu = (a) => setForm((f) => ({ ...f, anlagen: [...(f.anlagen || []).filter((x) => x.pfad !== a.pfad), a] }));
  const waehlen = (d) => {
    dazu({ name: d.name, pfad: d.path });
    merken(aussteller, d.path.replace(/\/[^/]+$/, ""));
    nachUpload && nachUpload();
    zeige(true, `Anlage „${d.name}“ hinzugefügt — weitere wählen oder „fertig“.`);
  };
  const hochladen = async (dateien) => {
    if (!dateien.length) return;
    setBusy(true);
    try {
      const rid = await sicherstellenId();
      for (const d of dateien) {
        const fd = new FormData(); fd.append("file", d);
        dazu(await json(await fetch(`${API}/${rid}/datei?art=anlage`, { method: "POST", body: fd })));
      }
      nachUpload && nachUpload();
      zeige(true, `${dateien.length} Anlage(n) hochgeladen — sie kommen hinten an die Rechnung.`);
    } catch (e) { zeige(false, String(e.message || e)); }
    setBusy(false);
  };

  const gewaehlt = new Set(anlagen.map((a) => a.pfad));
  const f = filter.trim().toLowerCase();
  const passt = (n) => !f || f.split(/\s+/).every((w) => n.toLowerCase().includes(w));
  const krumen = browser ? browser.pfad.slice(WURZEL.length).split("/").filter(Boolean) : [];
  const dateiKnopf = (d, unter) => (
    <button type="button" key={d.path} className={`rg-such-treffer${gewaehlt.has(d.path) ? " rg-gewaehlt" : ""}`}
      disabled={gewaehlt.has(d.path)} onClick={() => waehlen(d)} title={d.path}>
      <Ico n={gewaehlt.has(d.path) ? "haken" : "dokument"} /> {d.name}
      {unter && <span className="rg-baustein-vorschau">{unter}</span>}
    </button>
  );

  return (
    <div className="mb-sektion">
      <div className="mb-sektion-titel">Anlagen (PDF, hinten an die Rechnung)</div>
      {anlagen.length ? (
        <ul className="rg-anlagen">
          {anlagen.map((a, i) => (
            <li key={a.pfad}>
              <Ico n="klammer" /> <button type="button" className="mb-link" onClick={() => dropboxDatei(a.pfad).catch((e) => zeige(false, String(e.message || e)))}>{a.name}</button>
              {!gesperrt && <button type="button" className="mb-link" title="Anlage entfernen"
                onClick={() => setForm((x) => ({ ...x, anlagen: x.anlagen.filter((_, j) => j !== i) }))}><Ico n="schliessen" /></button>}
            </li>
          ))}
        </ul>
      ) : <p className="rg-klein" style={{ marginTop: 0 }}>Keine Anlagen — z. B. die Rechnungen, die weiterberechnet werden.</p>}
      {!gesperrt && (
        <div className="rg-knoepfe" style={{ marginTop: 6 }}>
          <button type="button" className="mb-btn" disabled={laedt} onClick={oeffnen}><Ico n="ordner" /> aus Dropbox wählen</button>
          <label className="mb-btn">{busy ? "lädt hoch …" : <><Ico n="hochladen" /> PDF hochladen</>}
            <input type="file" accept="application/pdf,.pdf" multiple hidden
              onChange={(e) => { const d = Array.from(e.target.files || []); e.target.value = ""; hochladen(d); }} />
          </label>
        </div>
      )}
      {browser && (
        <div className="rg-modal-hg" onClick={() => setBrowser(null)}>
          <div className="rg-modal rg-browser-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rg-modal-kopf">
              <div className="rg-krumen">
                <button type="button" className="mb-link" onClick={() => browse(WURZEL)}><Ico n="ordner" /> Holthey Immobilien</button>
                {krumen.map((k, i) => (
                  <span key={i}> / <button type="button" className="mb-link"
                    onClick={() => browse(`${WURZEL}/${krumen.slice(0, i + 1).join("/")}`)}>{k}</button></span>
                ))}
              </div>
              <button type="button" className="mb-link" title="schließen" onClick={() => setBrowser(null)}><Ico n="schliessen" /></button>
            </div>
            <div className="rg-browser-leiste">
              <div className="rg-suche">
                <input className="mb-input" value={filter} autoFocus placeholder="Suchen — Enter: auch in allen Unterordnern"
                  onChange={(e) => { setFilter(e.target.value); setTreffer(null); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); suchen(); } else if (e.key === "Escape") setBrowser(null); }} />
                <button type="button" className="mb-btn" disabled={laedt || filter.trim().length < 2} onClick={suchen}
                  title="Dropbox-Suche ab diesem Ordner"><Ico n="suche" /> in Unterordnern</button>
              </div>
              {start?.pfad && start.pfad !== browser.pfad && (
                <button type="button" className="mb-link" onClick={() => browse(start.pfad)}
                  title={start.pfad}><Ico n="ordner" /> {start.kreditoren ? `Kreditoren ${aussteller}` : `Ordner ${aussteller}`}</button>
              )}
            </div>
            <div className="rg-browser">
              {laedt && <div className="rg-such-leer">lädt …</div>}
              {treffer ? (
                <>
                  <div className="rg-klein" style={{ margin: "2px 4px" }}>{treffer.length} Treffer für „{filter.trim()}“ unterhalb von {browser.pfad.split("/").pop()}
                    {" "}<button type="button" className="mb-link" onClick={() => setTreffer(null)}>zurück zum Ordner</button></div>
                  {treffer.map((d) => dateiKnopf(d, d.ordner))}
                  {!treffer.length && <div className="rg-such-leer">Nichts gefunden.</div>}
                </>
              ) : (
                <>
                  {browser.pfad !== WURZEL && !f && (
                    <button type="button" className="rg-such-treffer" onClick={() => browse(browser.pfad.replace(/\/[^/]+$/, "") || WURZEL)}><Ico n="hoch" /> ..</button>
                  )}
                  {browser.ordner.filter((o) => passt(o.name)).map((o) => (
                    <button type="button" key={o.path} className="rg-such-treffer" onClick={() => browse(o.path)}><Ico n="ordner" /> {o.name}</button>
                  ))}
                  {browser.dateien.filter((d) => passt(d.name)).map((d) => dateiKnopf(d))}
                  {!browser.ordner.length && !browser.dateien.length && <div className="rg-such-leer">Leer (nur PDF-Dateien werden angezeigt).</div>}
                  {f && !browser.ordner.some((o) => passt(o.name)) && !browser.dateien.some((d) => passt(d.name)) && (
                    <div className="rg-such-leer">In diesem Ordner nichts — Enter sucht in allen Unterordnern.</div>
                  )}
                </>
              )}
            </div>
            <div className="rg-browser-fuss">
              <span className="rg-klein">{anlagen.length} Anlage(n) gewählt</span>
              <button type="button" className="mb-btn primary" onClick={() => setBrowser(null)}>fertig</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
